import type { SelectChangeEvent } from '@mui/material';
import { MenuItem, Select } from '@mui/material';
import type { InvoiceOverview } from '@vy/api-definition';
import { INVOICE_BILLING_STATUSES, INVOICE_REVIEW_STATUSES, type InvoiceBillingStatus } from '@vy/types';
import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { VyTooltip } from '../../../../../components/new-design/VyTooltip';
import { VyDialog } from '../../../../../components/new-design/feedback/VyDialog';
import { useUpdateInvoicesBillingStatuses } from '../../../../../hooks/requests/use-update-invoices-billing-statuses';
import { useDialog } from '../../../../../hooks/use-dialog';
import { queryKeys } from '../../../../../services/query-key/query-keys';
import { runMutation } from '../../../../../utils/vy-mutation';
import { getBillingStatusRestriction } from '../billing-status-restrictions';
import { getBillingStatusLabel, InvoiceBillingStatusLabel } from '../InvoiceBillingStatusLabel';
import { MarkAsPaidDialog } from '../MarkAsPaidDialog/MarkAsPaidDialog';

type InvoiceStatusSelectProps = {
  value: InvoiceBillingStatus;
  invoice: InvoiceOverview;
  tooltipContent?: React.ReactNode;
  showIndicator?: boolean;
};

export const InvoiceStatusSelect: React.FC<InvoiceStatusSelectProps> = ({
  value,
  invoice,
  tooltipContent,
  showIndicator,
}) => {
  const {
    isDialogOpen: isMarkAsPaidDialogOpen,
    handleClose: handleCloseMarkAsPaidDialog,
    handleOpen: handleOpenMarkAsPaidDialog,
  } = useDialog();
  const {
    isDialogOpen: isCancelConfirmDialogOpen,
    handleClose: handleCloseCancelConfirmDialog,
    handleOpen: handleOpenCancelConfirmDialog,
  } = useDialog();
  const [pendingBillingStatus, setPendingBillingStatus] = useState<InvoiceBillingStatus | null>(null);
  const updateInvoicesBillingStatusesMutation = useUpdateInvoicesBillingStatuses();
  const queryClient = useQueryClient();
  const isInvoiceApproved = invoice.reviewStatus === INVOICE_REVIEW_STATUSES.Approved;
  const isInvoiceCanceled = invoice.billingStatus === INVOICE_BILLING_STATUSES.Rejected;
  const isInvoicePaid = invoice.billingStatus === INVOICE_BILLING_STATUSES.Paid;

  const applyBillingStatus = async (billingStatus: InvoiceBillingStatus) => {
    const displayBillingStatus = getBillingStatusLabel(billingStatus);

    await runMutation(
      updateInvoicesBillingStatusesMutation,
      { invoiceStatus: billingStatus, invoiceIds: [invoice.id] },
      {
        pending: `Updating invoice status to ${displayBillingStatus}...`,
        success: `Invoice status updated to ${displayBillingStatus}`,
        error: `Failed to update invoice status to ${displayBillingStatus}`,
      },
      {
        onSuccess: async () => {
          await Promise.all([
            queryClient.invalidateQueries(queryKeys.invoicesOverviewsNew()),
            queryClient.invalidateQueries(queryKeys.invoiceStatistics()),
            queryClient.invalidateQueries(queryKeys.invoiceSummary()),
          ]);
        },
      },
    );
  };

  const handleStatusChange = async (event: SelectChangeEvent<string>) => {
    const billingStatus = event.target.value as InvoiceBillingStatus;

    if (billingStatus === INVOICE_BILLING_STATUSES.Paid) {
      handleOpenMarkAsPaidDialog();
      return;
    }

    if (billingStatus === INVOICE_BILLING_STATUSES.Rejected && isInvoiceApproved) {
      setPendingBillingStatus(billingStatus);
      handleOpenCancelConfirmDialog();
      return;
    }

    await applyBillingStatus(billingStatus);
  };

  const handleConfirmCancel = async () => {
    handleCloseCancelConfirmDialog();
    if (pendingBillingStatus) {
      await applyBillingStatus(pendingBillingStatus);
      setPendingBillingStatus(null);
    }
  };

  return (
    <div
      onClick={(event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        event.preventDefault();
      }}
    >
      <Select
        value={value}
        onChange={handleStatusChange}
        displayEmpty
        disableUnderline
        size="small"
        sx={{
          maxWidth: '1200px',
          '& .MuiSelect-icon': {
            color: 'black',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
        }}
        renderValue={(selectedStatus) => (
          <InvoiceBillingStatusLabel
            billingStatus={selectedStatus}
            isTrial={invoice.isTrial}
            tooltipContent={tooltipContent}
            showIndicator={showIndicator}
          />
        )}
      >
        {Object.values(INVOICE_BILLING_STATUSES).map((status) => {
          const { isDisabled, tooltipContent } = getBillingStatusRestriction(status, {
            isApproved: isInvoiceApproved,
            isPaid: isInvoicePaid,
            isCanceled: isInvoiceCanceled,
          });

          const menuItem = (
            <MenuItem key={status} value={status} disabled={isDisabled}>
              <InvoiceBillingStatusLabel
                billingStatus={status}
                sx={{
                  width: '100px !important',
                }}
              />
            </MenuItem>
          );

          if (isDisabled) {
            return (
              <VyTooltip key={status} tooltipContent={tooltipContent} placement="right-end">
                <div>{menuItem}</div>
              </VyTooltip>
            );
          }

          return menuItem;
        })}
      </Select>
      {isMarkAsPaidDialogOpen && (
        <MarkAsPaidDialog
          open={isMarkAsPaidDialogOpen}
          invoiceId={invoice.id}
          onClose={handleCloseMarkAsPaidDialog}
          customerName={invoice.customer.name}
          customerId={invoice.customer.id}
          currency={invoice.currency}
        />
      )}
      {isCancelConfirmDialogOpen && (
        <VyDialog
          open={isCancelConfirmDialogOpen}
          onClose={handleCloseCancelConfirmDialog}
          label="Cancel approved invoice"
          warningText="This action is irreversible. Once an approved invoice is cancelled, it cannot be un-cancelled."
          warningColor="#d32f2f"
          cancelButtonText="Go back"
          ctaText="Cancel invoice"
          ctaButtonProps={{ color: 'error' }}
          onSubmit={handleConfirmCancel}
          maxWidth="sm"
        />
      )}
    </div>
  );
};
